import { bindActionCreators } from 'redux';
import i18n from 'i18next';
import moment from 'moment';
import ColorConverter from 'color-convert';
import { saveSession } from './sessionHistory';
import { useEffect, useRef, useState } from 'react';

export const EPOCH = '1900-01-01T00:00:00Z';

export const SELECTION_PATH = '/main/selection';

export const MIN_TRANSECT_STEP = 10;
export const MAX_TRANSECT_STEP = 1000;
export const MIN_TRANSECT_EXTENT = 50;
export const MAX_TRANSECT_EXTENT = 5000;

export const TRANSECT_CONFIG = {
  CIRCLE_RADIUS: 1,
  POLYLINE_STYLE: {
    strokeColor: 'purple',
    strokeOpacity: 1.0,
    strokeWeight: 3,
    geodesic: true,
    clickable: true,
    zIndex: 90,
  },
};

/**
 * @function useLocalStorage
 * @description Custom React hook for managing state with localStorage persistence.
 * This hook retrieves the initial state from localStorage (if available) and
 * saves any updates to the state back to localStorage.
 * @param {string} key - The key under which the state is stored in localStorage.
 * @param {any} initialValue - The initial value to use if no value is found in localStorage.
 * @returns {Array<*>} A `[state, setState]` pair: the current state and a function to update it.
 */
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });
  const setValue = (value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };
  return [storedValue, setValue];
};

/**
 * @function useSessionStorage
 * @description `useState` that remembers its value for the rest of the visit, and only for that.
 * The counterpart of {@link useLocalStorage}, for a preference that should hold while the user
 * works - so that walking to the next step and back does not undo it - without becoming the way the
 * app greets them from then on.
 * @param {String} key - Where to keep it.
 * @param {*} initialValue - What to use when nothing was kept, which is every new visit.
 * **Called by:
 * @see {ImageTable} - which of the two image views is showing.
 * @memberof module:COMMON
 * @returns {Array} The value and its setter, as `useState` returns them.
 */
export const useSessionStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };

  return [storedValue, setValue];
};

/**
 * @constant SESSION_IMPORT_FLAG
 * @description sessionStorage key a session import raises on its way to a result page, for the
 * guided tours to stand down. Kept in sessionStorage rather than in Redux because the import
 * navigates as part of its own work: the page that has to read this is mounted by that navigation,
 * after the importer has finished.
 * @memberof module:COMMON
 */
export const SESSION_IMPORT_FLAG = 'sessionImportedResult';

/**
 * @function markSessionImported
 * @description Records that the next result page is being reached by replaying a session file
 * rather than by working through the flow.
 * **Called by:
 * @see {buildSessionImportConfigs} - on every module's importer.
 * @memberof module:COMMON
 * @returns {void}
 */
export const markSessionImported = () => {
  try {
    window.sessionStorage.setItem(SESSION_IMPORT_FLAG, 'true');
  } catch (error) {
    console.error(error);
  }
};

/**
 * @function useGuidedTour
 * @description Open/closed state for a page's guided tour, remembered across visits the way it
 * always was - with one exception: a page reached by loading a session file does not open its tour.
 * Someone restoring a finished analysis from JSON is not seeing the screen for the first time, and
 * being walked through the map while a completed result sits behind the overlay is noise. The
 * preference itself is left untouched, so the tour still opens on a genuine first visit later, and
 * the help button still opens it on demand.
 * The flag is read while deriving the initial state but cleared from an effect, so a double-invoked
 * initializer cannot consume it before the state that depends on it has been settled.
 * @param {String} storageKey - localStorage key holding the "show this tour" preference.
 * **Called by:
 * @see {ProcessingPage} - the Shoreline and Intertidal result pages.
 * @memberof module:COMMON
 * @returns {Array<*>} An `[isOpen, setOpen]` pair, interchangeable with the `useLocalStorage` pair
 *  these tours used before.
 */
export const useGuidedTour = (storageKey) => {
  const [remembered, setRemembered] = useLocalStorage(storageKey, true);

  const [isOpen, setIsOpen] = useState(() => {
    try {
      return remembered && window.sessionStorage.getItem(SESSION_IMPORT_FLAG) !== 'true';
    } catch (error) {
      console.error(error);
      return remembered;
    }
  });

  useEffect(() => {
    try {
      window.sessionStorage.removeItem(SESSION_IMPORT_FLAG);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const setTourOpen = (value) => {
    const next = value instanceof Function ? value(isOpen) : value;
    setIsOpen(next);
    setRemembered(next);
  };

  return [isOpen, setTourOpen];
};

/**
 * @function useScrollIntoViewOnOpen
 * @description Brings the element it is attached to into view when it opens. The panels on a result
 * page stack in a scrolling column, so one revealed by a button elsewhere on the page is added to
 * the bottom of that stack - on a shorter screen, below the visible part of it, which reads as the
 * button having done nothing at all.
 * Scrolls on the next frame, so the panel has been laid out and the column knows its real scroll
 * height before the distance is worked out, and only as far as it has to (`block: 'nearest'`) so a
 * panel already in view is left where it is.
 * @param {*} token - Truthy while the element should be revealed. A changing truthy value reveals it
 *  again, which is what a panel switched to a different subject needs.
 * **Called by:
 * @see {SidePanel} - MEPBay's details-and-export panel.
 * @returns {Object} Ref to attach to the element to reveal.
 */
export const useScrollIntoViewOnOpen = (token) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!token) return undefined;
    const frame = window.requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [token]);

  return ref;
};

/**
 * @function setLocalValue
 * @description Sets a value in localStorage after serializing it to JSON.
 * @param {string} key - The key under which the value will be stored in localStorage.
 * @param {any} value - The value to be stored (will be serialized to JSON).
 * @returns {void}
 */
export const setLocalValue = (key, value) => localStorage.setItem(key, JSON.stringify(value));

/**
 * @function getLocalValue
 * @description Retrieves a value from localStorage and deserializes it from JSON.
 * If the key does not exist, returns the provided default value.
 * @param {string} key - The key of the value to retrieve from localStorage.
 * @param {any} defaultValue - The default value to return if the key does not exist.
 * @returns {any} The retrieved value from localStorage or the default value.
 */
export const getLocalValue = (key, defaultValue) =>
  JSON.parse(localStorage.getItem(key)) ? JSON.parse(localStorage.getItem(key)) : defaultValue;

/**
 * @function fromEpoch
 * @description Adds a specified amount of time to a base epoch date and returns the result as a UTC moment object.
 * @param {number} value - The value to convert from epoch time.
 * @param {string} units - The units of time ... ('days', 'hours').
 * @returns {moment.Moment} A moment UTC date object representing the calculated date.
 * @example
 * const date = fromEpoch(7, 'days'); // Assuming EPOCH is moment('2020-01-01')
 * console.log(date.format('YYYY-MM-DD')); // Outputs: 2020-01-08
 */
export const fromEpoch = (value, units) => moment.utc(EPOCH).add(value, units);

/**
 * @function toEpoch
 * @description Converts a given date to its equivalent epoch time based on a epoch date.
 * @param {Date | string | moment.Moment} date - The date to convert to epoch time.
 * @param {string} units - The units of time ... ('years', 'months', 'weeks', 'days', 'hours',
 * @returns {number} The epoch time value representing the difference from the epoch date in the specified units.
 */
export const toEpoch = (date, units) => moment(date).diff(moment.utc(EPOCH), units);

/**
 * @function cloneClass
 * @description Creates a shallow clone of a class instance, preserving its prototype chain and properties.
 * @param {Object} original - The original class instance to clone.
 * @returns {Object} A new instance that is a shallow clone of the original.
 */
export const cloneClass = (original) => {
  return Object.assign(Object.create(Object.getPrototypeOf(original)), original);
};

export const formatArea = (area, places = 3) =>
  area >= 1e6 ? (area / 1e6).toFixed(places) + ' km²' : area.toFixed(places) + ' m²';

// centre is an array with 2 elements: [x, y]
export const makeTriangle = (centre, radius) => {
  const alpha = Math.PI / 2;
  const beta = alpha + (Math.PI * 2) / 3;
  const gamma = beta + (Math.PI * 2) / 3;

  const computePosition = (angle, axis) => {
    const segment = axis === 'x' ? Math.cos(angle) : Math.sin(angle);

    return segment * radius + centre[axis === 'x' ? 0 : 1];
  };

  const topX = computePosition(alpha, 'x');
  const topY = computePosition(alpha, 'y');

  const leftX = computePosition(beta, 'x');
  const leftY = computePosition(beta, 'y');

  const rightX = computePosition(gamma, 'x');
  const rightY = computePosition(gamma, 'y');

  return [
    [topX, topY],
    [leftX, leftY],
    [rightX, rightY],
  ];
};

export const lerp = (a, b, t) => a + t * (b - a);

export const normalize = (x, min, max) => (x - min) / (max - min);

export const asPercentage = (value) => (parseFloat(value) * 100).toFixed(2) + '%';

export const hasKeys = (object, ...keys) => {
  for (const key of keys) {
    if (!(key in object)) return false;
  }
  return true;
};

export const extractAlpha = (rgba) => {
  const res = rgba.match(/rgba\(.+,.+,.+,(.+)\)/);
  if (res && res[1]) return parseFloat(res[1]);
};

const EARTHS_RADIUS_M = 6371000;
const toRadians = (deg) => (deg * Math.PI) / 180;
const toDegrees = (rad) => (rad * 180) / Math.PI;

/**
 * @function computeBearingJS
 * @description Plain-JS equivalent of `algorithms/geodesy`'s `computeBearing` — that one is built
 * entirely on chained `ee.Number` calls, so it can't be reused anywhere that must stay
 * Earth-Engine-free (e.g. replaying an already-evaluated session client-side). Same great-circle
 * bearing formula, just with `Math.*` instead of `ee.Number.*`.
 * @param {Number} lng1
 * @param {Number} lat1
 * @param {Number} lng2
 * @param {Number} lat2
 * @memberof module:SHARED
 * @returns {Number} Initial bearing from point 1 to point 2, in radians.
 */
export const computeBearingJS = (lng1, lat1, lng2, lat2) => {
  const phi1 = toRadians(lat1);
  const phi2 = toRadians(lat2);
  const deltaLambda = toRadians(lng2) - toRadians(lng1);
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  return Math.atan2(y, x);
};

/**
 * @function computeDisplacementJS
 * @description Plain-JS equivalent of `algorithms/geodesy`'s `computeDisplacement` (see
 * `computeBearingJS`'s doc comment for why a separate implementation is needed).
 * @param {Number} lng - Starting point longitude.
 * @param {Number} lat - Starting point latitude.
 * @param {Number} theta - Bearing, in radians.
 * @param {Number} distance - Distance to displace by, in meters.
 * @memberof module:SHARED
 * @returns {[Number, Number]} The displaced point as `[lng, lat]`.
 */
export const computeDisplacementJS = (lng, lat, theta, distance) => {
  const phi1 = toRadians(lat);
  const lambda1 = toRadians(lng);
  const delta = distance / EARTHS_RADIUS_M;
  const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta));
  const lambda2 =
    lambda1 +
    Math.atan2(
      Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
      Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2),
    );
  return [((toDegrees(lambda2) + 540) % 360) - 180, toDegrees(phi2)];
};

/**
 * @function expandTransectToRectangle
 * @description Client-side (no Earth Engine) equivalent of `algorithms/shoreline/transects.js`'s
 * `expandHorizontally` — turns a 2-point transect LineString into the same thin rectangle Polygon
 * used for live-generated transects. Shared by Shoreline's and ShoreR's own `sessionExport.js`:
 * both draw an imported session's transects straight from the exported GeoJSON (deliberately
 * without touching Earth Engine again), and without this they stayed `LineString` features, which
 * `common/map.js`'s `addShape` renders through a different code path than the `Polygon` one
 * live-generated transects use — one with no click (open the evolution chart) or hover (erosion
 * classification tooltip) wired up.
 * @param {Object} feature - GeoJSON Feature, `geometry.type === 'LineString'` with exactly 2 points.
 * @param {Number} [amount=10] - Total rectangle width, in meters (matches `expandHorizontally`'s own default).
 * **Called by:
 * @see {renderPendingImportedShapes} - Shoreline's and ShoreR's own `sessionExport.js`.
 * @memberof module:SHARED
 * @returns {Object} A GeoJSON Feature with the same properties, `geometry.type === 'Polygon'`.
 */
export const expandTransectToRectangle = (feature, amount = 10) => {
  const half = amount / 2;
  const [a, b] = feature.geometry.coordinates;

  const theta = computeBearingJS(a[0], a[1], b[0], b[1]) + Math.PI;
  const alpha = theta + Math.PI * 0.5;
  const beta = theta - Math.PI * 0.5;

  const a1 = computeDisplacementJS(a[0], a[1], alpha, half);
  const a2 = computeDisplacementJS(a[0], a[1], beta, half);
  const b1 = computeDisplacementJS(b[0], b[1], alpha, half);
  const b2 = computeDisplacementJS(b[0], b[1], beta, half);

  return {
    ...feature,
    geometry: { type: 'Polygon', coordinates: [[a1, a2, b2, b1]] },
  };
};

export const organizeHierarchically = (object, separator = '_') => {
  const result = {};
  Object.keys(object).forEach((k) => {
    const [lhs, rhs] = k.match(new RegExp('(.*)' + separator + '(.+)'));
    if (result[lhs] === undefined) result[lhs] = {};
    result[lhs][rhs] = object[k];
  });

  return result;
};

/**
 * @function extentOf
 * @description The smallest and largest of a list of numbers. `Math.min(...values)` hands every
 * value to the call as an argument and overflows the call stack past about a hundred thousand of
 * them - the 175 908 soundings of a 50 m survey grid did.
 * @param {Number[]} values
 * @memberof module:Shared
 * @returns {Number[]} `[min, max]`; `[Infinity, -Infinity]` for an empty list.
 */
export const extentOf = (values) =>
  values.reduce(([min, max], value) => [value < min ? value : min, value > max ? value : max], [Infinity, -Infinity]);

export const sequence = (n, start = 0) => Array.from(Array(n).keys()).map((val) => val + start);

export const selectKey = (arr, key) => arr.map((value) => value[key]);

export const select = (obj, ...keys) => {
  const result = {};
  keys.forEach((key) => (result[key] = obj[key]));
  return result;
};

export const generateColors = (amount, lightness = 50) => {
  const slice = amount === 1 ? 0 : 240.0 / (amount - 1);
  return sequence(amount).map((i) => '#' + ColorConverter.hsl.hex(slice * i, 100, lightness));
};

export const BRBG_11 = [
  '#543005',
  '#8c510a',
  '#bf812d',
  '#dfc27d',
  '#f6e8c3',
  '#f5f5f5',
  '#c7eae5',
  '#80cdc1',
  '#35978f',
  '#01665e',
  '#003c30',
];

export const generateBrBGColors = (amount) => {
  if (amount <= 0) return [];
  if (amount === 1) return [BRBG_11[Math.floor(BRBG_11.length / 2)]];
  return sequence(amount).map((i) => {
    const t = i / (amount - 1);
    const pos = t * (BRBG_11.length - 1);
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, BRBG_11.length - 1);
    const frac = pos - i0;
    const c0 = ColorConverter.hex.rgb(BRBG_11[i0]);
    const c1 = ColorConverter.hex.rgb(BRBG_11[i1]);
    const rgb = c0.map((v, k) => Math.round(lerp(v, c1[k], frac)));
    return '#' + ColorConverter.rgb.hex(rgb);
  });
};

export const interpolateColors = (values, lowest, highest) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const absoluteMax = Math.max(Math.abs(min), Math.abs(max));
  lowest = ColorConverter.hex.rgb(lowest);
  highest = ColorConverter.hex.rgb(highest);
  return values.map((x) => {
    x = normalize(x, -absoluteMax, absoluteMax);
    const rgb = lowest.map((value, i) => lerp(value, highest[i], x));
    return '#' + ColorConverter.rgb.hex(rgb);
  });
};

export class Timer {
  constructor() {
    this.start = Date.now();
  }

  elapsed = () => Date.now() - this.start;

  restart = () => {
    const now = Date.now();
    const elapsed = now - this.start;
    this.start = now;
    return elapsed;
  };

  project = (x0, xn) => ((xn - x0) * this.elapsed()) / x0;

  format = (ms) => {
    const ref = Math.floor(ms / 1000);
    const mm = Math.floor(ref / 60);
    const ss = ref - mm * 60;
    return `${mm}m${ss}s`;
  };
}

export const datesBetween = (dates, start, end) =>
  dates.filter((date) => moment(date).isSameOrAfter(start) && moment(date).isSameOrBefore(end));

export const formatDate = (date, withTime = false) => moment(date).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');

export const formatLocaleDate = (date) => (date ? moment(date).format(i18n.t('formats.date')) : '');

export const dateDiff = (dateA, dateB, measure = 'years') => Math.abs(moment(dateA).diff(moment(dateB), measure));

export const formatDateDiff = (dateA, dateB) => {
  const years = dateDiff(dateA, dateB, 'years');

  if (years === 0) {
    const months = dateDiff(dateA, dateB, 'months');
    if (months === 0) {
      const days = dateDiff(dateA, dateB, 'days');
      return `${days} ${
        days === 1
          ? i18n.t('forms.acquisition.3.durationDays.singular')
          : i18n.t('forms.acquisition.3.durationDays.plural')
      }`;
    }
    return `${months} ${
      months === 1
        ? i18n.t('forms.acquisition.3.durationMonths.singular')
        : i18n.t('forms.acquisition.3.durationMonths.plural')
    }`;
  }
  return `${years} ${
    years === 1
      ? i18n.t('forms.acquisition.3.durationYears.singular')
      : i18n.t('forms.acquisition.3.durationYears.plural')
  }`;
};

export const firstValue = (object) => {
  const key = Object.keys(object)[0];
  return object[key];
};

export const bindDispatchTo = (...classes) => {
  return (dispatch) => {
    const bound = {};

    classes.forEach((Class) => {
      const instance = new Class(dispatch, Class.propName);
      bound[instance.name] = instance;
    });

    return bound;
  };
};

export const bindDispatch = (creators, actions = {}) => {
  return (dispatch) => {
    const bound = {};
    Object.keys(creators).forEach((key) => {
      bound[key.toLowerCase()] = bindActionCreators(creators[key], dispatch);
    });

    return { ...bound, ...bindActionCreators(actions, dispatch) };
  };
};

export const exportCoordinates = (data, fileName) => {
  let output = data.map((coordinate) => {
    return { lat: coordinate[0], long: coordinate[1] };
  });

  exportCSV(output, fileName);
};

/**
 * @function exportCSV
 * @description Exports data as a CSV file by converting the data to a CSV string
 * and triggering a download of the file with the specified name.
 *
 * @param {Object} data - The data to be exported as CSV.
 * @param {string} fileName - The name of the file to be downloaded (should include .csv extension).
 * @param {string} separator - The separator to be used in the CSV file (default is comma).
 *
 * @see {@link downloadText} - Function that creates and triggers the download of a text file
 *
 * * Called by:
 * @see  buildGeoJsonData - CoastlineEvolutionDialog.jsx
 * @see  exportCoordinates - utils.js
 *
 * @returns {void}
 */
export const exportCSV = (data, fileName, separator = ',') => {
  const columns = Object.keys(data[0]);

  let lines = columns.join(separator) + '\n';

  data.forEach((element) => {
    lines += columns.map((col) => element[col]).join(separator) + '\n';
  });

  downloadText(lines, fileName, 'text/csv');
};

/**
 * @function exportJSON
 * @description Exports data as a JSON file by converting the data to a JSON string
 * and triggering a download of the file with the specified name.
 *
 * @param {Object} data - The data to be exported as JSON.
 * @param {string} fileName - The name of the file to be downloaded (should include .json extension).
 *
 * @see {@link downloadText} - Function that creates and triggers the download of a text file
 *
 * * Called by:
 * @see  buildGeoJsonData - CoastlineEvolutionDialog.jsx
 *
 * @returns {void}
 */
export const exportJSON = (data, fileName) => {
  const json = JSON.stringify(data);
  downloadText(json, fileName, 'text/geojson');
};

/**
 * @function downloadText
 * @description Creates and triggers a download of a text file with the provided content.
 *
 * @param {string} text - The content to be included in the text file.
 * @param {string} fileName - The desired name for the downloaded file.
 * @param {string} mimeType - The MIME type of the file (default is "text/plain").
 *
 * * Called by:
 * @see  exportCSV 
 * @see  exportJSON 
 
 * @returns {void}
 */
export const downloadText = (text, fileName, mimeType = 'text/plain') => {
  const content = new Blob([text], { type: mimeType });
  const link = document.createElement('a');

  link.setAttribute('href', URL.createObjectURL(content));
  link.setAttribute('download', fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * @function normalizeNumericString
 * @description Cleans and standardizes strings
 * 1- Removes quotes at the start and end of the string.
 * 2- Replaces commas used as decimal separators with periods.
 *
 * @param {string} value - The input string to be cleaned.
 * * Called by:
 * @see  buildGeoJsonData - CoastlineEvolutionDialog.jsx
 *
 * @example
 * '"1,987,56"' (input) ->  '1.987.56' (output)
 *
 * @returns {string} The cleaned and standardized string.
 */
export const normalizeNumericString = (value) => {
  let cleaned = value.replace(/^"*|"*$/g, '');
  cleaned = cleaned.replace(/(\d),(\d)/g, '$1.$2');
  return cleaned;
};

/**
 * @function toTimestamp
 * @description Converts a Date object to a timestamp in milliseconds.
 * @param {Date} date - The date to convert.
 * @returns {number} The timestamp in milliseconds.
 * @example
 * const timestamp = toTimestamp(new Date('2023-01-01')) //Outputs: 1672531200000
 */
export const toTimestamp = (date) => parseInt(moment(date).format('x'), 10);

/**
 * @function toISOString
 * @description Converts a timestamp in milliseconds to an ISO 8601 string.
 * @param {number} timestamp - The timestamp in milliseconds.
 * @returns {string} The ISO 8601 string.
 * @example
 * const isoString = toISOString(1672531200000) //Outputs: '2023-01-01T00:00:00.000Z'
 */
export const toISOString = (timestamp) => moment(timestamp).toISOString();

/**
 * @function parseCsv
 * @description Parses a semicolon-delimited CSV string (with a header row) into an array of
 * plain row objects keyed by header.
 * @param {String} csvString - CSV content, first row as headers.
 * @memberof module:Csqueeze
 * @returns {Array<Object>} Parsed rows, one object per row.
 */
export const parseCsv = (csvString) => {
  const rows = csvString
    .trim()
    .split('\n')
    .map((row) => row.split(';'));
  const headers = rows[0];
  return rows.slice(1).map((row) => {
    const obj = {};
    row.forEach((value, columnIndex) => {
      obj[headers[columnIndex]] = value;
    });
    return obj;
  });
};

/**
 * @function useIsMounted
 * @description Tells a callback whether the component that owns it is still on screen.
 * A step that both starts something asynchronous and then navigates away needs this: the work
 * finishes after the step has been unmounted, and clearing its loading flag from a `finally` block
 * is a state update on a component that no longer exists - which React reports as a memory leak.
 * **Called by:
 * @see {AOIChooser} - the shared one and MEPBay's, both of which advance the flow mid-await.
 * @memberof module:SHARED
 * @returns {Object} A ref whose `current` is true while the component is mounted.
 */
export const useIsMounted = () => {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
};

/**
 * @constant SESSION_DATE_FIELDS
 * @description The fields of a session file that can say when its imagery is from, most telling
 * first. Every module names them differently - `chosenImages` here, `imageList` there,
 * `availableDates` in a third - so they are listed rather than guessed at.
 * The order matters as much as the list. The first group is what the analysis was actually built
 * from; the second is the pool it was chosen out of, which on a CSqueeze session is every candidate
 * the search turned up - eleven years of them for an analysis of two. Only consulted when the first
 * group says nothing.
 * @memberof module:SHARED
 */
const SESSION_DATE_FIELDS = [
  ['imageList', 'chosenImages', 'selectedImages', 'selectedImageDates', 'images', 'availableDates', 'yearTypes', 'years'],
  ['dates', 'fullImageList', 'period', 'periodData', 'imageDate', 'trainingDate'],
];

/**
 * @function collectYears
 * @description Every four-digit year buried in a value, however it is spelled: a full date
 * (`2018-03-01T13:00:00`), a tab key (`2018-1`), a bare year, or a number. Walks arrays and
 * objects, keys included, since a year is as often a key as a value.
 * @param {*} value - Anything off a session file.
 * @param {Set} [found] - Accumulator.
 * @param {Number} [depth] - Guard against walking a deep structure forever.
 * **Called by:
 * @see {sessionFileName}
 * @memberof module:SHARED
 * @returns {Set} The years found.
 */
const collectYears = (value, found = new Set(), depth = 0) => {
  if (value === null || value === undefined || depth > 6) return found;
  if (typeof value === 'number') {
    if (Number.isInteger(value) && value >= 1900 && value <= 2100) found.add(value);
    return found;
  }
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})(?:[-/]|$)/);
    if (match) {
      const year = Number(match[1]);
      if (year >= 1900 && year <= 2100) found.add(year);
    }
    return found;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => collectYears(entry, found, depth + 1));
    return found;
  }
  if (typeof value === 'object') {
    Object.keys(value).forEach((key) => collectYears(key, found, depth + 1));
    Object.values(value).forEach((entry) => collectYears(entry, found, depth + 1));
  }
  return found;
};

/**
 * @function slugify
 * @description Turns a place name into something safe for a file name: no accents, no spaces, no
 * extension, and short enough to leave the rest of the name readable.
 * @param {String} value - A name, typically the imported KML file name.
 * **Called by:
 * @see {sessionFileName}
 * @memberof module:SHARED
 * @returns {String} The slug, or an empty string when there is nothing usable.
 */
const slugify = (value) =>
  String(value || '')
    .replace(/\.[^.]+$/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .replace(/-+$/, '');

/**
 * @function placeFromCoordinates
 * @description Names a place by where it is, for a session with no file name to borrow one from:
 * the middle of the area, as degrees and hemisphere - `25s048w`.
 * @param {Array<Array<Number>>} coordinates - The area, as `[lng, lat]` pairs.
 * **Called by:
 * @see {sessionFileName}
 * @memberof module:SHARED
 * @returns {String} The place, or an empty string when there is no area.
 */
const placeFromCoordinates = (coordinates) => {
  const points = (coordinates || []).filter((point) => Array.isArray(point) && point.length >= 2);
  if (!points.length) return '';
  const average = (index) => points.reduce((total, point) => total + Number(point[index]), 0) / points.length;
  const lng = average(0);
  const lat = average(1);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return "";
  const lattitude = `${String(Math.round(Math.abs(lat))).padStart(2, '0')}${lat < 0 ? 's' : 'n'}`;
  const longitude = `${String(Math.round(Math.abs(lng))).padStart(3, '0')}${lng < 0 ? 'w' : 'e'}`;
  return `${lattitude}${longitude}`;
};

/**
 * @function sessionSummary
 * @description What an analysis is about, in the three things that tell one apart from another: the
 * module it belongs to, where its area is, and the years its imagery covers.
 * Read from the file itself rather than stored alongside it, so it works the same for a session
 * saved today and one exported months ago.
 * @param {Object} session - The snapshot, as `buildSessionExport` returns it.
 * @param {String} [kmlFileName] - The imported KML's name, when the caller has it.
 * **Called by:
 * @see {sessionFileName} - to name the downloaded file.
 * @see {exportSession} - to file it in the history under something readable.
 * @memberof module:SHARED
 * @returns {Object} `{module, place, period}`, each possibly empty.
 */
/**
 * @function sessionFileName
 * @description Names a session export after what is in it: the module, where the area is, and the
 * years the imagery covers - `csqueeze-paranagua-2018-2019.json`.
 * Every module used to write one fixed name, so a second export of anything replaced the first, or
 * piled up as `(1)`, `(2)` in the downloads folder with nothing to tell them apart.
 * The place is the imported KML's name when there is one, since that is what the user called the
 * area themselves; otherwise it is the middle of the area in degrees.
 * @param {Object} session - The object about to be written, as `buildSessionExport` returns it.
 * @param {String} [kmlFileName] - The imported KML's name, when the page has it to hand.
 * **Called by:
 * @see {ProcessingPage} - every module's result page, at its export button.
 * @memberof module:SHARED
 * @returns {String} A file name ending in `.json`.
 */
export const sessionSummary = (session, kmlFileName) => {
  let years = new Set();
  SESSION_DATE_FIELDS.some((group) => {
    years = new Set();
    group.forEach((field) => collectYears(session?.[field], years));
    return years.size > 0;
  });
  const sorted = [...years].sort((a, b) => a - b);
  return {
    module: session?.module || 'cassie',
    place: slugify(kmlFileName || session?.kmlFileName) || placeFromCoordinates(session?.coordinates),
    period: sorted.length
      ? [sorted[0], sorted[sorted.length - 1]].filter((year, index, all) => index === 0 || year !== all[0]).join('-')
      : '',
  };
};

export const sessionFileName = (session, kmlFileName) => {
  const { module, place, period } = sessionSummary(session, kmlFileName);
  return [module, place, period || new Date().toISOString().slice(0, 10)].filter(Boolean).join('-') + '.json';
};

/**
 * @function exportSession
 * @description Hands a session to the browser under a name that says what it holds, and keeps a
 * copy in the analysis history so reopening it later does not depend on finding the file again.
 * Filing it is deliberately not allowed to fail the download: the file is what the user asked for,
 * and a browser that refuses storage should not turn that into an error.
 * @param {Object} session - The object to write, as `buildSessionExport` returns it.
 * @param {String} [kmlFileName] - The imported KML's name, when the page has it.
 * @see {@link sessionFileName}
 * **Called by:
 * @see {ProcessingPage} - every module's result page, at its export button.
 * @memberof module:SHARED
 * @returns {void}
 */
export const exportSession = (session, kmlFileName) => {
  exportJSON(session, sessionFileName(session, kmlFileName));
  const { place, period } = sessionSummary(session, kmlFileName);
  saveSession(session, { title: place, detail: period }).catch((error) =>
    console.warn('[SessionHistory] the analysis was downloaded but not filed:', error),
  );
};
